import {
  ChangeEvent,
  LegacyRef,
  useCallback,
  useEffect,
  useRef,
  useState,
  KeyboardEvent,
  // RefObject,
} from 'react'
import { SvgClearCross } from '../../assets/images/index'
import { debounce, isEmpty, noop, isEqual, get } from 'lodash'
import {
  Controller,
  FieldErrorsImpl,
  FieldValues,
  GlobalError,
} from 'react-hook-form'
import {
  ControlledDropDownPropTypes,
  DropdownType,
  NAMESPACE,
} from '../../utils'
import ReactDOM from 'react-dom'
import SvgDropdownArrowGrey from '../../assets/images/components/SvgDropdownArrowGrey'
interface OutsideClickHandlerProps {
  refs: React.RefObject<HTMLElement>[]
  handler: () => void
}

const useOutsideClick = ({ refs, handler }: OutsideClickHandlerProps) => {
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      // Check if click is on scrollbar
      const isScrollbarClick =
        event.clientX > document.documentElement.offsetWidth ||
        event.clientY > document.documentElement.offsetHeight

      if (isScrollbarClick) return
      if (
        !refs?.some((ref: React.RefObject<HTMLElement>) =>
          ref?.current?.contains(event.target as Node)
        )
      ) {
        handler()
      }
    }

    document.addEventListener('mousedown', handleClickOutside)

    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [refs, handler])
}

interface NestedOptionsType<T> extends React.RefAttributes<HTMLDivElement> {
  isNestedOpen: boolean | undefined
  nestedOptions: DropdownType<T, string>[]
  position: {
    top: number
    left: number
    width: number
  }
  optionZIndex: number
  portalContainer: HTMLElement
  onOptionClick: (
    option: DropdownType<T, string> | null,
    nestedOption?: DropdownType<T, string> | null | undefined
  ) => void
  option: DropdownType<T, string> | null
  handleNestedOptions?: (
    parentOption?: DropdownType<T> | null,
    page?: number,
    limit?: number
  ) => Promise<DropdownType<T>[]>
  setNestedOptions: React.Dispatch<
    React.SetStateAction<DropdownType<T, string>[]>
  >
  scrollPositionRef: React.MutableRefObject<number>
  setPage: React.Dispatch<React.SetStateAction<number>>
  page?: number
}
const NestedOptions = <T,>({
  isNestedOpen,
  nestedOptions,
  position,
  optionZIndex,
  portalContainer,
  onOptionClick,
  option,
  setNestedOptions,
  handleNestedOptions,
  scrollPositionRef,
  setPage,
}: NestedOptionsType<T>) => {
  const nestedDropdownOptionRef = useRef<HTMLDivElement>(null)
  const [loading, setLoading] = useState(false)
  const [hasMore, setHasMore] = useState(true)
  const [nestedOptionsLength, setNestedOptionsLength] = useState(
    position.width * 2
  )
  const handleArrowPosition = (width: number) => {
    setNestedOptionsLength(width)
  }
  const handleMouseDown = (
    e: React.MouseEvent<HTMLLIElement, MouseEvent>,
    nestedOption: DropdownType<T, string>
  ) => {
    e.preventDefault()
    e.stopPropagation()
    onOptionClick(option, nestedOption)
  }
  const fetchItems = async (page: number, limit: number) => {
    if (loading || !hasMore || !handleNestedOptions) return

    setLoading(true)
    try {
      const response = await handleNestedOptions(option, page, limit)
      if (response.length > 0) {
        setNestedOptions((options) => [...options, ...response])
      }
      if (limit && response.length < limit) {
        setHasMore(false)
      }
    } catch (error) {
      console.error('Error fetching data:', error)
    } finally {
      setLoading(false)
    }
  }

  const handleScroll = () => {
    const dropdownMenu = nestedDropdownOptionRef.current
    if (!dropdownMenu) return

    const scrollTop = dropdownMenu.scrollTop
    const scrollHeight = dropdownMenu.scrollHeight
    const offsetHeight = dropdownMenu.offsetHeight

    if (scrollTop + offsetHeight >= scrollHeight - 10 && hasMore && !loading) {
      scrollPositionRef.current = scrollTop
      setPage &&
        setPage((prevPage) => {
          const newPage = prevPage + 1
          fetchItems(newPage, 10)
          return newPage
        })
    }
  }

  useEffect(() => {
    if (nestedDropdownOptionRef.current) {
      nestedDropdownOptionRef.current.addEventListener('scroll', handleScroll)
    }

    return () => {
      if (nestedDropdownOptionRef.current) {
        nestedDropdownOptionRef.current.removeEventListener(
          'scroll',
          handleScroll
        )
      }
    }
  }, [loading, hasMore])

  useEffect(() => {
    if (nestedDropdownOptionRef.current) {
      nestedDropdownOptionRef.current.scrollTop = scrollPositionRef.current
    }
  }, [nestedOptions])
  return (
    <>
      <span
        className={`max-w-96 truncate font-light text-xs flex items-center ${
          position.left + position?.width + nestedOptionsLength >
          window.innerWidth
            ? 'gap-3'
            : 'justify-between'
        }  w-full`}
      >
        {position.left + position?.width + nestedOptionsLength >
          window.innerWidth &&
          nestedOptions?.length > 0 && (
            <SvgDropdownArrowGrey
              className={`transform transition-transform duration-200  ${
                isNestedOpen ? 'rotate-90' : 'rotate-180'
              } `}
              fill={'#000000'}
              aria-hidden="true"
            />
          )}
        {option?.label}

        {position.left + position?.width + nestedOptionsLength <=
          window.innerWidth &&
          nestedOptions?.length > 0 && (
            <SvgDropdownArrowGrey
              className={`transform transition-transform duration-200  ${
                isNestedOpen ? '-rotate-90' : 'rotate-180'
              } `}
              fill={'#000000'}
              aria-hidden="true"
            />
          )}
      </span>
      {isNestedOpen && nestedOptions?.length > 0
        ? ReactDOM.createPortal(
            <NestedOptionsContainer
              handleMouseDown={handleMouseDown}
              loading={loading}
              nestedDropdownOptionRef={nestedDropdownOptionRef}
              nestedOptions={nestedOptions}
              optionZIndex={optionZIndex}
              position={position}
              handleArrowPosition={handleArrowPosition}
            />,
            portalContainer
          )
        : null}
    </>
  )
}
interface NestedOptionsContainerType<T> {
  nestedDropdownOptionRef: React.RefObject<HTMLDivElement>
  position: {
    top: number
    left: number
    width: number
  }
  optionZIndex: number
  nestedOptions: DropdownType<T, string>[]
  handleMouseDown: (
    e: React.MouseEvent<HTMLLIElement, MouseEvent>,
    nestedOption: DropdownType<T, string>
  ) => void

  loading: boolean
  handleArrowPosition: (width: number) => void
}
const NestedOptionsContainer = <T,>({
  nestedDropdownOptionRef,
  position,
  optionZIndex,
  nestedOptions,
  handleMouseDown,
  loading,
  handleArrowPosition,
}: NestedOptionsContainerType<T>) => {
  const [nestedOptionsLength, setNestedOptionsLength] = useState(
    position.width * 2
  )
  const [visibility, setVisibility] = useState(false)

  useEffect(() => {
    setVisibility(false)
    const nestedDropdownOptionRefRect =
      nestedDropdownOptionRef?.current?.getBoundingClientRect()

    const width = nestedDropdownOptionRefRect?.width
    setNestedOptionsLength(width ?? 0)
    handleArrowPosition(width ?? 0)
    setVisibility(true)
  }, [position])

  return (
    <div
      ref={nestedDropdownOptionRef}
      style={{
        position: 'absolute',
        top: position.top,
        left: position.left + position.width,
        zIndex: optionZIndex,
        ...(position.left + nestedOptionsLength + position.width >
          window.innerWidth && {
          left: 'auto',
          right: window.innerWidth - position.left,
        }),
        minWidth: position.width + 'px',
        visibility: visibility ? 'visible' : 'hidden',
      }}
      onClick={(e) => e.stopPropagation()}
      onMouseDown={(e) => e.stopPropagation()}
      className=" font-extraLight overflow-y-auto mt-1 rounded-md shadow-lg bg-white border border-gray-200 w-fulloverflow-y-auto max-h-[18rem]"
    >
      <div className="">
        <ul className="">
          {nestedOptions?.map((nestedOption, index) => (
            <li
              key={index}
              data-qa-id={(index + 1).toString()}
              className="px-4 py-2 hover:bg-gray-50 hover:text-gray-950 cursor-pointer w-full text-gray-600 text-xs"
              title={nestedOption.label}
              role="option"
              aria-selected={false}
              tabIndex={-1}
              onMouseDown={(e) => handleMouseDown(e, nestedOption)}
            >
              <span className="max-w-96 truncate font-semibold text-xs">
                {nestedOption.label}
              </span>
            </li>
          ))}
        </ul>
        {loading && (
          <div className="text-center p-2 text-accent-500 font-light text-xs">
            Loading...
          </div>
        )}
      </div>
    </div>
  )
}

interface OptionContainerPropsType<T> {
  idOption: number
  option: DropdownType<T> | null
  handleValueSelect: (
    e: DropdownType<T>,
    nestedOption?: DropdownType<T, string>
  ) => void
  isSelected: boolean
  isHighlighted: boolean
  customStyles?: object
  nestedSelectedIndex?: number
  handleNestedSelect?: (index: number) => void
  handleNestedOptions?: (
    parentOption?: DropdownType<T> | null,
    page?: number,
    limit?: number
  ) => Promise<DropdownType<T>[]>
  optionZIndex: number
  triggerParentSelect?: { trigger: boolean }
  setTriggerParentSelect: React.Dispatch<
    React.SetStateAction<{
      trigger: boolean
    }>
  >
}

const OptionContainer = <T,>({
  idOption,
  option,
  handleValueSelect,
  isSelected,
  isHighlighted,
  customStyles = {},
  handleNestedSelect,
  handleNestedOptions,
  optionZIndex,
  nestedSelectedIndex,
  triggerParentSelect,
  setTriggerParentSelect,
}: OptionContainerPropsType<T>) => {
  const scrollPositionForNestedRef = useRef<number>(0)
  const [currentPage, setCurrentPage] = useState(0)
  const [nestedOptions, setNestedOptions] = useState<DropdownType<T>[]>([])
  const [openNested, setOpenNested] = useState(false)
  const optionRef = useRef<HTMLLIElement>(null)
  const nestedDropdownRef = useRef<HTMLDivElement>(null)
  const [position, setPosition] = useState({ top: 0, left: 0, width: 200 })
  const portalContainer =
    document.getElementById('nested-dropdown') || document.createElement('div')
  if (!document.getElementById('nested-dropdown')) {
    portalContainer.id = 'nested-dropdown'
    document.body.appendChild(portalContainer)
  }
  useOutsideClick({
    refs: [optionRef, nestedDropdownRef],
    handler: () => {
      setOpenNested(false)
      setNestedOptions([])
      handleNestedSelect && handleNestedSelect(-1)
    },
  })

  const handleMouseDown = async (
    e: React.MouseEvent<HTMLLIElement, MouseEvent>
  ) => {
    e?.preventDefault()
    try {
      if (nestedSelectedIndex !== idOption - 1 && handleNestedOptions) {
        const nestedData = await handleNestedOptions(option, 0, 10)
        setNestedOptions(nestedData)
        setOpenNested(true)
        handleNestedSelect && handleNestedSelect(idOption - 1)
        setCurrentPage(0)
        return
      }
      if (openNested && handleNestedOptions) {
        setOpenNested(false)
        setNestedOptions([])
        handleNestedSelect && handleNestedSelect(-1)
        return
      }
      if (!isEmpty(option) && handleNestedOptions && !openNested) {
        const nestedData = await handleNestedOptions(option, 0, 10)
        setNestedOptions(nestedData)
        setOpenNested(true)
        handleNestedSelect && handleNestedSelect(idOption - 1)
        setCurrentPage(0)
      } else {
        !isEmpty(option) && handleValueSelect(option)
      }
    } catch (error) {
      console.error('Error in handleMouseDown:', error)
    }
  }

  const handleKeyDown = async (e: KeyboardEvent<HTMLLIElement>) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault()
      try {
        if (nestedSelectedIndex !== idOption - 1 && handleNestedOptions) {
          const nestedData = await handleNestedOptions(option)
          setNestedOptions(nestedData)
          setOpenNested(true)
          handleNestedSelect && handleNestedSelect(idOption - 1)
          return
        }
        if (openNested && handleNestedOptions) {
          setOpenNested(false)
          setNestedOptions([])
          handleNestedSelect && handleNestedSelect(-1)
          return
        }
        if (!isEmpty(option) && handleNestedOptions && !openNested) {
          const nestedData = await handleNestedOptions(option)
          setNestedOptions(nestedData)
          setOpenNested(true)
          handleNestedSelect && handleNestedSelect(idOption - 1)
        } else {
          !isEmpty(option) && handleValueSelect(option)
        }
      } catch (error) {
        console.error('Error in handleMouseDown:', error)
      }
    }
  }

  const handleNestedOptionClick = (
    option: DropdownType<T, string> | null,
    nestedOption?: DropdownType<T, string> | null
  ) => {
    if (option && nestedOption) {
      handleValueSelect(option, nestedOption)
    } else if (option) {
      handleValueSelect(option)
    }
  }

  useEffect(() => {
    if (openNested) {
      const optionRect = optionRef.current?.getBoundingClientRect()
      if (optionRect) {
        setPosition({
          top: optionRect.top + window.scrollY,
          left: optionRect.left + window.scrollX,
          width: optionRect.width,
        })
      }
    }
  }, [openNested])
  useEffect(() => {
    if (triggerParentSelect?.trigger && isHighlighted) {
      optionRef.current?.dispatchEvent(
        new MouseEvent('mousedown', { bubbles: true })
      )
      setTriggerParentSelect({ trigger: false })
    }
  }, [triggerParentSelect])

  const renderOptionContent = () => {
    if (
      nestedSelectedIndex === idOption - 1 &&
      nestedOptions?.length > 0 &&
      openNested
    ) {
      return (
        <NestedOptions
          ref={nestedDropdownRef}
          isNestedOpen={openNested}
          nestedOptions={nestedOptions ?? []}
          position={position}
          optionZIndex={optionZIndex}
          portalContainer={portalContainer}
          onOptionClick={handleNestedOptionClick}
          option={option}
          setNestedOptions={setNestedOptions}
          handleNestedOptions={handleNestedOptions}
          scrollPositionRef={scrollPositionForNestedRef}
          setPage={setCurrentPage}
          page={currentPage}
        />
      )
    }

    if (option?.renderedComponent) {
      return option.renderedComponent as React.ReactNode
    }

    return (
      <span className="max-w-96 truncate font-light text-xs">
        {option?.label}
      </span>
    )
  }

  return (
    <li
      ref={optionRef}
      data-qa-id={idOption?.toString()}
      className={`p-3 border-t border-border-100 hover:bg-secondary-25 flex pointer-events-auto cursor-pointer w-full text-black font-primary ${
        isSelected ? 'bg-secondary-300' : ''
      } ${isHighlighted ? 'bg-secondary-100' : ''}`}
      title={option?.tooltip?.toString() ?? option?.label}
      onMouseDown={handleMouseDown}
      onKeyDown={handleKeyDown}
      role="option"
      aria-selected={isSelected}
      tabIndex={-1}
      style={{ ...customStyles }}
    >
      {renderOptionContent()}
    </li>
  )
}

interface OptionBoxPropsType<T> {
  options: DropdownType<T>[]
  excludedParentRef: React.RefObject<HTMLElement>
  handleValueSelect: (
    e: DropdownType<T>,
    nestedOption?: DropdownType<T> | null
  ) => void
  handleOptionFalse: () => void
  toggleOption: () => void
  isOpenOptions: boolean
  selectedOption: DropdownType<T> | null
  fetchOptions?: (page: number, limit: number) => Promise<Response>
  page?: number
  setPage?: React.Dispatch<React.SetStateAction<number>>
  limit?: number
  virtualScroll?: boolean
  optionZIndex: number
  position: {
    top: number
    left: number
    width: number
  }
  scrollPositionRef: React.MutableRefObject<number>
  optionsScrollRef?: React.RefObject<HTMLDivElement>
  isCombobox?: boolean
  highlightedIndex: number
  customStyles?: object
  handleNestedOptions?: (
    parentOption?: DropdownType<T> | null,
    page?: number,
    limit?: number
  ) => Promise<DropdownType<T>[]>
  triggerParentSelect: {
    trigger: boolean
  }
  setTriggerParentSelect: React.Dispatch<
    React.SetStateAction<{
      trigger: boolean
    }>
  >
}

interface SearchInput<T> {
  selectedOption: DropdownType<T> | null
  placeholder: string
  disabled: boolean
  handleOptionSearch: (
    e: ChangeEvent<HTMLInputElement>,
    keepOptionsClosed?: boolean
  ) => void
  inpref: LegacyRef<HTMLInputElement>
  hasPermission: boolean
  initialValue?: string
  tabIndex?: number
  customStyles?: object
  tooltip?: string
  externalValue?: string
}

const SearchInput = <T,>({
  selectedOption,
  placeholder,
  disabled,
  handleOptionSearch,
  inpref,
  hasPermission,
  initialValue,
  tabIndex,
  externalValue,
  customStyles = {},
}: SearchInput<T>) => {
  const [selectedValue, setSelectedValue] = useState(
    selectedOption?.label || initialValue || ''
  )

  const handleOnChange = (e: ChangeEvent<HTMLInputElement>) => {
    const fieldValue = e?.currentTarget?.value
    if (externalValue === undefined) {
      setSelectedValue(fieldValue)
    }

    handleOptionSearch(e)
  }
  const handleBlurChange = () => {
    if (externalValue === undefined) {
      setSelectedValue(selectedOption?.label ?? initialValue ?? '')
      // if (
      //   !selectedOption?.label &&
      //   !initialValue &&
      //   (inpref as RefObject<HTMLInputElement>)?.current?.value
      // ) {
      //   handleOptionSearch(
      //     {
      //       currentTarget: { value: selectedOption?.label ?? initialValue ?? '' },
      //     } as ChangeEvent<HTMLInputElement>,
      //     true
      //   )
      // }
    }
  }

  useEffect(() => {
    if (externalValue === undefined) {
      setSelectedValue(selectedOption?.label ?? initialValue ?? '')
    }
  }, [selectedOption, initialValue, externalValue])
  return (
    <input
      ref={inpref}
      type="text"
      placeholder={placeholder}
      title={selectedValue}
      className={` outline-none border-none box-content bg-transparent m-0 block min-w-0 w-full font-light text-base sm:text-xs pt-3.5 pb-3 ${
        disabled ? 'text-accent-500' : 'text-current'
      } h-5 `}
      disabled={disabled}
      style={{ ...customStyles }}
      onChange={handleOnChange}
      value={
        hasPermission
          ? externalValue !== undefined
            ? externalValue
            : selectedValue
          : NAMESPACE.REDACTED
      }
      onBlur={handleBlurChange}
      tabIndex={disabled ? -1 : tabIndex || 0}
      spellCheck={false}
      aria-autocomplete="list"
      aria-expanded={true}
      aria-controls="options-list"
    />
  )
}

const OptionBox = <T,>({
  options,
  excludedParentRef,
  handleValueSelect,
  handleOptionFalse,
  toggleOption,
  isOpenOptions,
  selectedOption,
  virtualScroll,
  fetchOptions,
  setPage,
  limit,
  optionZIndex,
  position,
  scrollPositionRef,
  isCombobox = false,
  highlightedIndex,
  customStyles = {},
  handleNestedOptions,
  triggerParentSelect,
  setTriggerParentSelect,
  optionsScrollRef,
}: OptionBoxPropsType<T>) => {
  const dropdownOptionRef = useRef<HTMLDivElement>(null)
  const [loading, setLoading] = useState(false)
  const [hasMore, setHasMore] = useState(true)
  const [nestedOptionIndex, setNestedOptionIndex] = useState(-1)
  useOutsideClick({
    refs: [excludedParentRef, dropdownOptionRef],
    handler: handleOptionFalse,
  })
  const handleNestedSelect = (index: number) => {
    setNestedOptionIndex(index)
  }
  const fetchItems = async (page: number, limit: number) => {
    if (loading || !hasMore || !fetchOptions) return

    setLoading(true)
    try {
      const response = await fetchOptions(page, limit)
      const data: DropdownType<T>[] = await response.json()

      if (limit && data.length < limit) {
        setHasMore(false)
      }
    } catch (error) {
      console.error('Error fetching data:', error)
    } finally {
      setLoading(false)
    }
  }

  const handleScroll = () => {
    const dropdownMenu = dropdownOptionRef.current
    if (!dropdownMenu) return

    const scrollTop = dropdownMenu.scrollTop
    const scrollHeight = dropdownMenu.scrollHeight
    const offsetHeight = dropdownMenu.offsetHeight

    if (scrollTop + offsetHeight >= scrollHeight - 10 && hasMore && !loading) {
      scrollPositionRef.current = scrollTop
      setPage &&
        setPage((prevPage) => {
          const newPage = prevPage + 1
          fetchItems(newPage, limit ?? 0)
          return newPage
        })
    }
  }

  useEffect(() => {
    if (dropdownOptionRef.current && virtualScroll) {
      dropdownOptionRef.current.addEventListener('scroll', handleScroll)
    }

    return () => {
      if (dropdownOptionRef.current && virtualScroll) {
        dropdownOptionRef.current.removeEventListener('scroll', handleScroll)
      }
    }
  }, [loading, hasMore])

  useEffect(() => {
    if (isOpenOptions && highlightedIndex >= 0 && dropdownOptionRef.current) {
      const optionElements = dropdownOptionRef.current.querySelectorAll('li')
      const highlightedElement = optionElements[highlightedIndex]

      if (highlightedElement) {
        const containerRect = dropdownOptionRef.current.getBoundingClientRect()
        const elementRect = highlightedElement.getBoundingClientRect()

        const isAbove = elementRect.top < containerRect.top
        const isBelow = elementRect.bottom > containerRect.bottom

        if (isAbove || isBelow) {
          highlightedElement.scrollIntoView({
            block: 'nearest',
            behavior: 'smooth',
          })
        }
      }
    }
  }, [highlightedIndex, isOpenOptions])

  const handleDivRef = useCallback((element: HTMLDivElement | null) => {
    if (element && dropdownOptionRef) {
      ;(
        dropdownOptionRef as React.MutableRefObject<HTMLDivElement | null>
      ).current = element
    }
    if (element && optionsScrollRef) {
      ;(
        optionsScrollRef as React.MutableRefObject<HTMLDivElement | null>
      ).current = element
    }
  }, [])

  return (
    <div
      ref={handleDivRef}
      onClick={toggleOption}
      className={` font-extraLight overflow-y-auto mt-[0.006rem] mr-5 rounded-b  border-r border-l border-b bg-accent-0 max-h-[12.375rem]  group-hover:border-t-0
              ${isOpenOptions ? 'border-secondary-500' : ' border-accent-600'}
              group-hover:border-secondary-500`}
      style={{
        position: 'absolute',
        top: position.top,
        left: position.left,
        minWidth: position.width + 'px',
        zIndex: optionZIndex,
        ...customStyles,
      }}
      id="options-list" // ARIA: ID for the options list
      role="listbox" // ARIA: Role for the options container
      aria-activedescendant={
        highlightedIndex >= 0 ? `option-${highlightedIndex}` : undefined
      } // ARIA: Active option
    >
      <div className="">
        <ul className="">
          {isEmpty(options) && !isCombobox ? (
            <li
              className={`p-3 border-t border-border-100 flex pointer-events-auto cursor-not-allowed w-full text-accent-500`}
              role="option"
              aria-disabled="true"
            >
              <span
                className="max-w-[calc(100%-0.938rem)] truncate font-light text-xs"
                style={{ ...customStyles }}
              >
                No Options
              </span>
            </li>
          ) : (
            options?.map((option: DropdownType<T>, index: number) => (
              <OptionContainer
                customStyles={{ ...customStyles }}
                idOption={index + 1}
                key={`${option?.value}_${index}`}
                option={option}
                handleValueSelect={handleValueSelect}
                isSelected={isEqual(selectedOption?.value, option?.value)}
                isHighlighted={highlightedIndex === index}
                handleNestedSelect={handleNestedSelect}
                handleNestedOptions={handleNestedOptions}
                optionZIndex={optionZIndex}
                nestedSelectedIndex={nestedOptionIndex}
                triggerParentSelect={triggerParentSelect}
                setTriggerParentSelect={setTriggerParentSelect}
              />
            ))
          )}
          {loading && (
            <div
              className="text-center p-2 text-accent-500 font-light text-xs"
              style={{ ...customStyles }}
            >
              Loading...
            </div>
          )}
        </ul>
      </div>
    </div>
  )
}

interface FieldErrors<U extends FieldValues> {
  root?: Record<string, GlobalError> & GlobalError
  [key: string]: FieldErrorsImpl<U> | GlobalError | undefined
}

interface SelectDropDownPropTypes<T, U extends FieldValues> {
  dataQaId?: string
  label: string
  options: DropdownType<T>[]
  name: string
  defaultValue?: string | null
  disabled?: boolean
  required?: boolean
  onChange: (
    value: DropdownType<T>,
    name: string,
    nestedOption?: DropdownType<T>
  ) => void
  onFocus?: () => void
  placeholder?: string
  className?: string
  errors?: FieldErrors<U>
  isSearchable?: boolean
  isBordered?: boolean
  isCustomSearch?: boolean
  onSearchChange?: (value: string) => void
  isCrossable?: boolean
  fetchOptions?: (page: number, limit: number) => Promise<Response>
  page?: number
  setPage?: React.Dispatch<React.SetStateAction<number>>
  limit?: number
  virtualScroll?: boolean
  optionZIndex?: number
  hasPermission?: boolean
  icon?: (
    disabled: boolean,
    closeDropdownOptions: () => void,
    tabIndex?: number
  ) => React.ReactElement
  showSvgDropdownArrow?: boolean
  isCombobox?: boolean
  isFilter?: boolean
  confirmationModal?: (value: string | null) => Promise<boolean | undefined>
  tabIndex?: number
  customStyles?: object
  height?: string
  customPadding?: string
  borderClassName?: string
  searchLength?: number
  tooltip?: string
  isPreviewAllowed?: {
    value: boolean
    className: string
    onClick: (e?: React.MouseEvent<HTMLSpanElement, MouseEvent>) => void
  }
  handleNestedOptions?: (
    parentOption?: DropdownType<T> | null,
    page?: number,
    limit?: number
  ) => Promise<DropdownType<T>[]>
  dropdownArrowColor?: string
  checkPosition?: boolean
  customLabelIndex?: number
  allowCloseOnScroll?: boolean
  enableTypeToJump?: boolean
  enableTypeToFilter?: boolean
  minimumCharactersToFilter?: number
}

export const SelectDropDown = <T, U extends FieldValues>({
  dataQaId,
  label = '',
  name = '',
  options,
  defaultValue,
  disabled = false,
  required = false,
  onChange = noop,
  onFocus = noop,
  placeholder = 'Select Option',
  className = '',
  errors,
  isSearchable = false,
  isBordered = true,
  isCustomSearch = false,
  onSearchChange = noop,
  isCrossable = true,
  fetchOptions,
  limit,
  virtualScroll,
  optionZIndex = 50,
  hasPermission = true,
  icon,
  showSvgDropdownArrow = true,
  isCombobox = false,
  isFilter,
  confirmationModal,
  tabIndex,
  customStyles = {},
  height = 'h-[1.25rem]',
  customPadding = '',
  borderClassName = '',
  searchLength = 3,
  isPreviewAllowed = { value: false, className: '', onClick: noop },
  handleNestedOptions,
  dropdownArrowColor = '#0A6FCD',
  checkPosition = true,
  customLabelIndex = 30,
  allowCloseOnScroll = false,
  enableTypeToJump = false,
  enableTypeToFilter = false,
  minimumCharactersToFilter = 1,
}: SelectDropDownPropTypes<T, U>) => {
  const [isComboboxSearching, setIsComboboxSearching] = useState<boolean>(false)
  const [isOpenOptions, setIsOpenOptions] = useState<boolean>(false)
  const textFieldRef = useRef<HTMLDivElement>(null)
  const [position, setPosition] = useState({ top: 0, left: 0, width: 200 })
  const [selectedOption, setSelectedOption] = useState<DropdownType<T> | null>(
    null
  )
  const [currentPage, setCurrentPage] = useState(0)
  const [searchResultOption, setSearchResultOption] = useState<
    DropdownType<T>[]
  >(options || [])
  const [triggerParentSelect, setTriggerParentSelect] = useState({
    trigger: false,
  })
  const scrollPositionRef = useRef<number>(0)
  const [highlightedIndex, setHighlightedIndex] = useState(-1) // For keyboard navigation
  const [cumulativeSearchInput, setCumulativeSearchInput] = useState<string>('') // For Type-to-Jump
  const typeheadTimeoutRef = useRef<NodeJS.Timeout | null>(null)

  const inpref = useRef<HTMLInputElement>(null)
  const dropdownRef = useRef<HTMLDivElement>(null)
  const optionsScrollRef = useRef<HTMLDivElement>(null)
  const prevOptionsRef = useRef<DropdownType<T>[] | null>(null)
  const [isJustOpened, setIsJustOpened] = useState(false)

  const clearButtonRef = useRef<HTMLDivElement>(null)

  const isSearchableFromSettings = useCallback(
    (dropdownOptions: DropdownType<T>[]): boolean => {
      if (!dropdownOptions || dropdownOptions.length === 0) {
        return false
      }
      return dropdownOptions.some((option) => {
        const hasSearchableSetting =
          (option as { settings?: { isSearchable?: boolean } })?.settings
            ?.isSearchable === true
        const hasDirectSearchableFlag =
          (option as { isSearchable?: boolean })?.isSearchable === true
        return hasSearchableSetting || hasDirectSearchableFlag
      })
    },
    []
  )

  const shouldBeSearchable =
    isSearchable ||
    isSearchableFromSettings(options) ||
    enableTypeToJump ||
    enableTypeToFilter

  const toggleOption = () => {
    setTimeout(() => {
      setIsOpenOptions((prevOption) => !prevOption)
    }, 0)
    setHighlightedIndex(-1) // Reset highlight when opening/closing
  }

  const handleFocusInput = () => {
    setCumulativeSearchInput('')
    if (inpref?.current) {
      inpref.current.focus()
    }
    setIsJustOpened(true)
    setIsOpenOptions(true)
    setHighlightedIndex(-1)
    setTimeout(() => {
      setIsJustOpened(false)
    }, 200)
  }

  const blurInput = () => {
    inpref?.current?.blur()
  }

  const handleOptionFalse = useCallback(() => {
    if (isJustOpened) {
      return
    }
    setIsOpenOptions(false)
    setHighlightedIndex(-1)
  }, [isJustOpened])

  const clearAll = async () => {
    if (confirmationModal) {
      const confirmed = await confirmationModal(null)
      if (!confirmed) return
    }

    setSelectedOption(null)
    setCumulativeSearchInput('')
    onChange({ label: '', value: '' }, name)

    // Keep focus on dropdown after clearing
    setTimeout(() => {
      if (shouldBeSearchable) {
        inpref?.current?.focus()
      } else {
        dropdownRef.current?.focus()
      }
    }, 0)
  }
  const getScrollableParents = (element: HTMLElement | null): HTMLElement[] => {
    const scrollable: HTMLElement[] = []

    const findParents = (el: HTMLElement | null) => {
      if (!el) return

      const style = getComputedStyle(el)
      const overflow = `${style.overflow}${style.overflowX}${style.overflowY}`

      if (/(auto|scroll|overlay)/.test(overflow)) {
        scrollable.push(el)
      }

      findParents(el.parentElement)
    }

    findParents(element)

    return scrollable
  }

  const dropdown = dropdownRef.current

  const scrollParents = getScrollableParents(dropdown || null)

  allowCloseOnScroll &&
    scrollParents.forEach((parent) => {
      parent.addEventListener('scroll', () => {
        setIsOpenOptions(false)
      })
    })

  const handleValueSelect = async (
    selectedValue: DropdownType<T>,
    nestedOption?: DropdownType<T> | null
  ) => {
    if (confirmationModal) {
      const confirmed = await confirmationModal(selectedValue.value)
      if (!confirmed) return
    }
    const cleanedSelectedOption = { ...selectedValue }
    if (cleanedSelectedOption.renderedComponent)
      delete cleanedSelectedOption.renderedComponent
    setSelectedOption(structuredClone(cleanedSelectedOption))
    if (nestedOption) {
      onChange(selectedValue, name, nestedOption)
    } else {
      onChange(selectedValue, name)
    }
    blurInput()
    setTimeout(() => {
      setIsOpenOptions(false)
    }, 0)
    setHighlightedIndex(-1) // Reset highlight when opening/closing

    // Return focus to dropdown after selection
    setTimeout(() => {
      if (shouldBeSearchable) {
        inpref?.current?.focus()
      } else {
        dropdownRef.current?.focus()
      }
    }, 0)
  }

  const debouncedHandleInputChange = useCallback(
    debounce((fieldValue) => {
      const lowerFieldValue = fieldValue?.toLowerCase()

      if (
        lowerFieldValue.length >= searchLength ||
        lowerFieldValue.length === 0
      ) {
        if (isCustomSearch) {
          if (isCombobox) {
            onSearchChange(fieldValue)
          } else {
            onSearchChange(lowerFieldValue)
          }
        } else {
          const selectOptions = options.filter((option) =>
            option?.label?.toLowerCase()?.includes(lowerFieldValue)
          )
          setSearchResultOption(selectOptions || [])
        }
      }
    }, 699),
    [options, isCustomSearch, isCombobox, onSearchChange]
  )

  const handleOptionSearch = (
    e: ChangeEvent<HTMLInputElement>,
    keepOptionsClosed = false
  ) => {
    debouncedHandleInputChange(e?.currentTarget?.value)
    setHighlightedIndex(-1) // Reset highlight on search
    if (isCombobox) {
      setIsComboboxSearching(true)
      return
    }
    if (!keepOptionsClosed) setIsOpenOptions(true)
  }
  const checkIfSelectedOptionEmpty = (
    selectedOption: DropdownType<T, string> | null
  ) => {
    return selectedOption?.value !== null && selectedOption?.value !== undefined
  }

  useEffect(() => {
    // Handle clicks outside the dropdown
    const handleDocumentClick = (e: MouseEvent) => {
      if (!isOpenOptions) return

      const clickedDropdown = dropdownRef.current?.contains(e.target as Node)
      const clickedOptions = document
        .getElementById('dropdown-root')
        ?.contains(e.target as Node)

      if (!clickedDropdown && !clickedOptions) {
        setIsOpenOptions(false)
        setHighlightedIndex(-1)
      }
    }

    // Handle focus changes (for tabbing)
    const handleFocusChange = (e: FocusEvent) => {
      if (!isOpenOptions) return

      // Check if focus moved outside both dropdown and its options
      const focusInDropdown = dropdownRef.current?.contains(e.target as Node)
      const focusInOptions = document
        .getElementById('dropdown-root')
        ?.contains(e.target as Node)
      const focusInClearButton = clearButtonRef.current?.contains(
        e.target as Node
      )

      if (!focusInDropdown && !focusInOptions && !focusInClearButton) {
        setIsOpenOptions(false)
        setHighlightedIndex(-1)
      }
    }
    const handleVisibilityChange = () => {
      if (document.hidden) {
        setIsOpenOptions(false)
        setHighlightedIndex(-1)
      }
    }

    document.addEventListener('visibilitychange', handleVisibilityChange)
    document.addEventListener('mousedown', handleDocumentClick)
    document.addEventListener('focusin', handleFocusChange)

    return () => {
      document.removeEventListener('mousedown', handleDocumentClick)
      document.removeEventListener('focusin', handleFocusChange)
      document.removeEventListener('visibilitychange', handleVisibilityChange)
    }
  }, [isOpenOptions])

  const updatePosition = useCallback(
    (preventClose?: boolean) => {
      if (!isOpenOptions || !textFieldRef.current) return
      const textFieldRect = textFieldRef.current.getBoundingClientRect()
      const hasError = errors?.[name]
      if (
        checkPosition &&
        textFieldRect.bottom + window.scrollY < 110 &&
        !preventClose &&
        !hasError
      ) {
        handleOptionFalse()
        return
      }

      setPosition({
        top: textFieldRect.bottom + window.scrollY,
        left: textFieldRect.left + window.scrollX,
        width: textFieldRect.width,
      })
    },
    [isOpenOptions, errors, name, checkPosition, handleOptionFalse]
  )

  useEffect(() => {
    if (!isOpenOptions) return

    const handleScrollOrResize = (e: Event) => {
      const target = e.target as HTMLElement
      if (
        target?.parentElement?.id === 'dropdown-root' ||
        target?.id === 'dropdown-root'
      ) {
        return
      }
      requestAnimationFrame(() => {
        updatePosition()
      })
    }

    window.addEventListener('scroll', handleScrollOrResize, true)
    window.addEventListener('resize', handleScrollOrResize)

    return () => {
      window.removeEventListener('scroll', handleScrollOrResize, true)
      window.removeEventListener('resize', handleScrollOrResize)
    }
  }, [isOpenOptions, updatePosition])

  useEffect(() => {
    if (isOpenOptions) {
      requestAnimationFrame(() => {
        updatePosition(true)
      })
    } else {
      setSearchResultOption(options || [])
    }
  }, [isOpenOptions, updatePosition])

  useEffect(() => {
    if (!isOpenOptions || !dropdownRef.current) return

    const adjustDropdownPosition = () => {
      const dropdowns = document.querySelectorAll<HTMLElement>(
        '#dropdown-root .dropdown-menu, #dropdown-root .dropdown-list, #dropdown-root [role="listbox"], #nested-dropdown .dropdown-menu, #nested-dropdown .dropdown-list, #nested-dropdown [role="listbox"]'
      )

      dropdowns.forEach((dropdown) => {
        const triggerRect = dropdownRef.current?.getBoundingClientRect()
        const dropdownRect = dropdown.getBoundingClientRect()

        if (!dropdown.dataset.originalMaxHeight) {
          dropdown.dataset.originalMaxHeight = dropdown.style.maxHeight ?? ''
        }

        if (dropdownRect.bottom > window.innerHeight) {
          const availableHeightBelow =
            window.innerHeight - (triggerRect?.bottom ?? 0)
          dropdown.style.maxHeight = `${availableHeightBelow}px`
          dropdown.style.overflowY = 'auto'
        } else if (dropdownRect.top < 0) {
          const availableHeightAbove = dropdownRect.bottom
          dropdown.style.maxHeight = `${availableHeightAbove}px`
          dropdown.style.overflowY = 'auto'
        } else {
          dropdown.style.maxHeight = dropdown.dataset.originalMaxHeight
          dropdown.style.overflowY = ''
        }

        dropdown.style.position = 'fixed'
        dropdown.style.top = `${triggerRect?.bottom ?? 0}px`
        dropdown.style.left = `${triggerRect?.left ?? 0}px`
        dropdown.style.minWidth = `${triggerRect?.width ?? 0}px`
        dropdown.style.width = 'auto'
      })
    }

    const initialAdjust = () => requestAnimationFrame(adjustDropdownPosition)
    const timeoutId = window.setTimeout(initialAdjust, 0)

    const observer = new MutationObserver(adjustDropdownPosition)
    const dropdownRoot = document.getElementById('dropdown-root')
    const nestedRoot = document.getElementById('nested-dropdown')
    if (dropdownRoot)
      observer.observe(dropdownRoot, { childList: true, subtree: true })
    if (nestedRoot)
      observer.observe(nestedRoot, { childList: true, subtree: true })

    let rafId: number
    const onScroll = () => {
      if (rafId) cancelAnimationFrame(rafId)
      rafId = requestAnimationFrame(adjustDropdownPosition)
    }
    window.addEventListener('scroll', onScroll, true)

    return () => {
      window.removeEventListener('scroll', onScroll, true)
      observer.disconnect()
      clearTimeout(timeoutId)
      if (rafId) cancelAnimationFrame(rafId)
    }
  }, [isOpenOptions])

  useEffect(() => {
    if (isCombobox) {
      if (defaultValue) {
        setSelectedOption(defaultValue as unknown as DropdownType<T>)
      }
    }
  }, [defaultValue, isCombobox])

  useEffect(() => {
    if (isCombobox) {
      setSearchResultOption(options || [])
      return
    }
    if (!isEmpty(options)) {
      const selected = options?.filter((option: DropdownType<T>) =>
        isEqual(option?.value, defaultValue)
      )
      setSelectedOption(selected?.[0] || null)
    } else {
      setSelectedOption(null)
    }
    if (!isEqual(prevOptionsRef.current, options)) {
      setSearchResultOption(options || [])
    }
    prevOptionsRef.current = options
  }, [defaultValue, isCombobox, options])

  // in case of combobox close the options instead of showing 'no options' and open only when there are options
  useEffect(() => {
    if (!isCombobox) return

    if (isComboboxSearching && !isEmpty(options)) {
      // we check for options list not empty here as custom search would update options in case of combobox
      setIsOpenOptions(true)
    } else {
      setIsOpenOptions(false)
    }
  }, [options, isCombobox, isComboboxSearching])

  // close combobox searching if isOpenOptions is closed by some event
  useEffect(() => {
    if (isCombobox && !isOpenOptions) {
      // isCombobox is redundant here but added for clarity
      setIsComboboxSearching(false)
    }
  }, [isOpenOptions, isCombobox])

  const handleMouseMove = () => {
    if (isEmpty(searchResultOption)) {
      setSearchResultOption([])
    }
  }

  useEffect(() => {
    if (shouldBeSearchable) {
      window.addEventListener('mousemove', handleMouseMove)
      return () => {
        window.removeEventListener('mousemove', handleMouseMove)
      }
    }
  }, [searchResultOption, shouldBeSearchable])

  useEffect(() => {
    if (!isOpenOptions) {
      setCumulativeSearchInput('')
      setSearchResultOption(options)
      if (typeheadTimeoutRef.current) {
        clearTimeout(typeheadTimeoutRef.current)
      }
    }
  }, [isOpenOptions])

  // Type to jump
  const handleTypeToJump = (input: string) => {
    const matchingIndex = searchResultOption.findIndex((option) =>
      option.label.toLowerCase().startsWith(input.toLowerCase())
    )
    if (matchingIndex !== -1) {
      setHighlightedIndex(matchingIndex)
    }
  }

  // Type to filter - filters the visible list
  const handleTypeToFilter = (input: string) => {
    const filteredOptions = options.filter((option) =>
      option.label.toLowerCase().includes(input.toLowerCase())
    )
    setSearchResultOption(filteredOptions.length > 0 ? filteredOptions : [])
    setHighlightedIndex(filteredOptions.length > 0 ? 0 : -1)
  }

  const handleKeyDown = (e: KeyboardEvent) => {
    if (disabled) {
      return
    }
    if (
      (e.key === 'Backspace' || e.key === 'Delete') &&
      (enableTypeToJump || enableTypeToFilter)
    ) {
      if (
        cumulativeSearchInput.length === 0 &&
        selectedOption?.value &&
        (e.key === 'Backspace' || e.key === 'Delete')
      ) {
        e.preventDefault()
        setSelectedOption(null)
        onChange({ label: '', value: '' }, name)
        setCumulativeSearchInput('')
        setSearchResultOption(options)
        setHighlightedIndex(-1)
        if (optionsScrollRef.current) {
          optionsScrollRef.current.scrollTop = 0
        }
        return
      }

      if (cumulativeSearchInput.length > 0) {
        const newInput =
          e.key === 'Backspace' ? cumulativeSearchInput.slice(0, -1) : ''

        setCumulativeSearchInput(newInput)

        if (enableTypeToFilter) {
          if (newInput.length >= minimumCharactersToFilter) {
            handleTypeToFilter(newInput)
          } else {
            setSearchResultOption(options)
            setHighlightedIndex(-1)
            if (newInput.length === 0) {
              setSelectedOption(null)
              onChange({ label: '', value: '' }, name)
              if (optionsScrollRef.current) {
                optionsScrollRef.current.scrollTop = 0
              }
            }
          }
        } else if (enableTypeToJump) {
          if (newInput.length > 0) {
            handleTypeToJump(newInput)
          } else {
            setSearchResultOption(options)
            setHighlightedIndex(-1)
            setSelectedOption(null)
            onChange({ label: '', value: '' }, name)
            if (optionsScrollRef.current) {
              optionsScrollRef.current.scrollTop = 0
            }
          }
        }
      }

      return
    }

    const isPrintableChar =
      e.key.length === 1 && !e.ctrlKey && !e.metaKey && !e.altKey

    if (
      isPrintableChar &&
      isOpenOptions &&
      (enableTypeToJump || enableTypeToFilter)
    ) {
      e.preventDefault()

      // build new cumulative input FIRST
      const newInput =
        cumulativeSearchInput.length === 0
          ? e.key
          : cumulativeSearchInput + e.key
      setCumulativeSearchInput(newInput)

      // reset timeout
      //COMMENTING OUT THE TIMEOUT FOR NOW AS IT CREATES A BAD UX WHEN USER TYPES SLOWLY, WE CAN REVISIT THIS LATER WITH BETTER UX SOLUTION LIKE SHOWING THE TYPED INPUT SOMEWHERE IN THE UI
      // if (typeheadTimeoutRef.current) clearTimeout(typeheadTimeoutRef.current)
      // typeheadTimeoutRef.current = setTimeout(() => {
      //   setCumulativeSearchInput('')
      //   // restore full list when input clears
      //   if (enableTypeToFilter) setSearchResultOption(options)
      // }, 1000)
      if (enableTypeToFilter) setSearchResultOption(options)

      if (enableTypeToJump && enableTypeToFilter) {
        // hybrid: jump until min chars reached, then filter
        if (newInput.length < minimumCharactersToFilter) {
          handleTypeToJump(newInput)
        } else {
          handleTypeToFilter(newInput)
        }
      } else if (enableTypeToFilter) {
        if (newInput.length >= minimumCharactersToFilter) {
          handleTypeToFilter(newInput)
        }
      } else if (enableTypeToJump) {
        handleTypeToJump(newInput)
      }

      return
    }

    // Open dropdown on Enter or ArrowDown
    if ((e.key === 'Enter' || e.key === 'ArrowDown') && !isOpenOptions) {
      e.preventDefault()
      setIsOpenOptions(true)
      if (!isEmpty(searchResultOption) && e.key === 'ArrowDown') {
        setHighlightedIndex(0)
      }
      return
    }

    // Close dropdown on Escape
    if (e.key === 'Escape' && isOpenOptions) {
      e.preventDefault()
      setIsOpenOptions(false)
      setHighlightedIndex(-1)
      return
    }

    // Navigation within dropdown
    if (isOpenOptions && !isEmpty(searchResultOption)) {
      if (e.key === 'ArrowDown') {
        e.preventDefault()
        setHighlightedIndex((prevIndex) =>
          Math.min(prevIndex + 1, searchResultOption.length - 1)
        )
      } else if (e.key === 'ArrowUp') {
        e.preventDefault()
        setHighlightedIndex((prevIndex) =>
          prevIndex === -1 ? 0 : Math.max(prevIndex - 1, 0)
        )
      } else if (e.key === 'Enter' && highlightedIndex !== -1) {
        e.preventDefault()
        handleValueSelect(searchResultOption[highlightedIndex])
      } else if (e.key === 'Tab') {
        // Close dropdown on Tab
        setIsOpenOptions(false)
        setHighlightedIndex(-1)
        // Let default tab behavior continue
      }
    }
  }

  const portalContainer =
    document.getElementById('dropdown-root') || document.createElement('div')

  if (!document.getElementById('dropdown-root')) {
    portalContainer.id = 'dropdown-root'
    document.body.appendChild(portalContainer)
  }

  return (
    <div
      ref={dropdownRef}
      data-qa-id={dataQaId ?? name ?? ''}
      className={`group relative w-full font-primary rounded
        ${
          disabled
            ? 'cursor-not-allowed bg-accent-75'
            : 'cursor-pointer select-none'
        }
        focus:outline-none
        focus:border-transparent
      `}
      onFocus={onFocus}
      onClick={
        isEmpty(options)
          ? noop
          : disabled
          ? noop
          : shouldBeSearchable
          ? handleFocusInput
          : toggleOption
      }
      onKeyDown={handleKeyDown}
      tabIndex={disabled ? -1 : tabIndex || 0}
      aria-haspopup="listbox"
      aria-expanded={isOpenOptions}
      aria-labelledby={`${name}-label`}
      style={{ ...customStyles }}
      role="combobox"
    >
      <div
        className={`w-full box-border relative items-center select-wrap rounded ${className} focus-within:outline-none`}
        ref={textFieldRef}
      >
        {label ? (
          <div
            className={`${
              selectedOption?.value || placeholder || isOpenOptions
                ? 'translate-x-[0.875rem] t translate-y-[-0.75rem]'
                : 'translate-x-[0.875rem] t translate-y-[0.5rem]'
            } transition-all duration-100 ease-in-out flex text-base px-1 origin-top-left whitespace-nowrap overflow-hidden text-ellipsis max-w-[calc(100%-1.75rem)] absolute left-0 top-0 z-${customLabelIndex} bg-white ${
              disabled ? 'text-accent-300' : 'text-accent-900'
            }  `}
            id={`${name}-label`}
            style={{ ...customStyles }}
          >
            <label
              className={`font-extraLight text-xs leading-1 block whitespace-nowrap overflow-hidden text-ellipsis max-w-[calc(100% - 1.5rem)
                ${isOpenOptions ? 'text-secondary-500' : ''}
                ${
                  disabled
                    ? 'text-accent-300'
                    : 'group-hover:text-secondary-500 group-focus-within:text-secondary-500'
                }`}
              title={label}
              htmlFor={name}
              style={{ ...customStyles }}
            >
              {label}
            </label>
            {required ? (
              <span
                className={`ml-1 text-xs leading-1  ${
                  disabled ? 'text-accent-300' : 'text-error-500'
                }`}
                style={{ ...customStyles }}
              >
                *
              </span>
            ) : null}
          </div>
        ) : null}
        <div
          className={`z-[2] ${
            isFilter ? 'pl-8' : 'pl-4'
          } pr-2.5 pt-2 pb-2 relative w-full ${
            !disabled ? 'wf-view-wrap' : 'pt-1'
          } ${customPadding}`}
        >
          <div
            data-qa-id={`add-field-dropdown`}
            className={`flex items-center justify-between w-full ${height} wf-view`}
          >
            {shouldBeSearchable ? (
              <SearchInput
                key={selectedOption?.value?.toString() ?? 'empty'}
                customStyles={{ ...customStyles }}
                selectedOption={selectedOption}
                placeholder={placeholder}
                disabled={disabled}
                handleOptionSearch={handleOptionSearch}
                inpref={inpref}
                hasPermission={hasPermission}
                initialValue={isCombobox ? (defaultValue as string) : ''}
                tabIndex={tabIndex}
                externalValue={
                  enableTypeToJump || enableTypeToFilter
                    ? cumulativeSearchInput || undefined
                    : undefined
                }
              />
            ) : (
              <span
                data-qa-id={`dropdown_${name}`}
                className={`font-light truncate max-w-[calc(100%-0.938rem)] text-base sm:text-xs ${
                  selectedOption?.value && !disabled
                    ? 'text-black'
                    : 'text-accent-500'
                } ${disabled ? 'text-accent-500' : ''} w-full ${
                  isPreviewAllowed?.value === true && selectedOption
                    ? `${isPreviewAllowed?.className}`
                    : ''
                }`}
                title={
                  selectedOption?.tooltip?.toString() ??
                  selectedOption?.label ??
                  placeholder
                }
                onClick={(e) =>
                  selectedOption?.label && isPreviewAllowed?.onClick?.(e)
                }
                style={{ ...customStyles }}
              >
                {(enableTypeToJump || enableTypeToFilter) &&
                cumulativeSearchInput ? (
                  <span className="text-secondary-500">
                    {cumulativeSearchInput}
                  </span>
                ) : (
                  selectedOption?.label ?? placeholder
                )}
              </span>
            )}

            <div className="flex gap-2 items-center p-2">
              {isCrossable &&
              checkIfSelectedOptionEmpty(selectedOption) &&
              !disabled ? (
                <div
                  ref={clearButtonRef}
                  className="p-1"
                  onClick={(e) => {
                    e.stopPropagation()
                    if (!disabled) clearAll()
                  }}
                  tabIndex={disabled ? -1 : tabIndex ?? 0}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault()
                      e.stopPropagation()
                      if (!disabled) clearAll()
                    }
                  }}
                  role="button"
                  aria-label="Clear selection"
                >
                  <SvgClearCross />
                </div>
              ) : null}
              {icon
                ? icon(disabled, handleOptionFalse, tabIndex)
                : showSvgDropdownArrow && (
                    <SvgDropdownArrowGrey
                      className={`transform transition-transform duration-200  ${
                        isOpenOptions ? 'rotate-180' : ''
                      } ${disabled ? 'visible' : ''}`}
                      fill={`${disabled ? '' : dropdownArrowColor}`}
                      aria-hidden="true"
                    />
                  )}
            </div>
          </div>
        </div>

        <fieldset
          aria-hidden="true"
          className={`text-left ${isBordered ? 'border' : ''} absolute ${
            label
              ? selectedOption?.value || placeholder || isOpenOptions
                ? 'top-[-0.938rem]'
                : 'top-0'
              : 'top-0'
          } bottom-0 left-0 right-0 px-2 pointer-events-none rounded border-solid overflow-hidden min-w-0 font-extraLight outline-none  
                    border-border-150
                 
                    ${
                      isOpenOptions
                        ? 'border-b-0 rounded-b-none group-hover:border-b-0 group-hover:rounded-b-none border-secondary-500'
                        : ''
                    }
                    ${
                      disabled
                        ? 'border-border-200 bg-accent-50'
                        : 'group-hover:border-secondary-500 group-focus-within:border-secondary-500 group-focus-within:border'
                    }
                    ${
                      !isEmpty(errors?.[name])
                        ? 'border-error-500'
                        : 'border-accent-150'
                    } ${borderClassName}`}
        >
          {label ? (
            <legend
              className={`${
                selectedOption?.value || placeholder || isOpenOptions
                  ? 'invisible'
                  : 'hidden'
              } z-[2]  bg-white float-none max-w-full select-none  p-1  `}
            >
              <span
                className={` dropdown-label overflow-hidden whitespace-nowrap  text-ellipsis font-extraLight text-xs 
            ${isOpenOptions ? 'text-secondary-500' : ''}
            ${
              disabled
                ? 'text-accent-300'
                : 'group-hover:text-secondary-500 group-focus-within:text-secondary-500'
            }`}
                style={{ ...customStyles }}
              >
                {label}
              </span>
              {required ? (
                <span
                  className={`ml-1 text-xs leading-1  ${
                    disabled ? 'text-accent-300' : 'text-error-500'
                  }`}
                  style={{ ...customStyles }}
                >
                  *
                </span>
              ) : null}
            </legend>
          ) : null}
        </fieldset>
      </div>

      {isOpenOptions
        ? ReactDOM.createPortal(
            <OptionBox
              customStyles={{ ...customStyles }}
              excludedParentRef={dropdownRef}
              handleValueSelect={handleValueSelect}
              handleOptionFalse={handleOptionFalse}
              toggleOption={toggleOption}
              options={searchResultOption}
              isOpenOptions={isOpenOptions}
              selectedOption={selectedOption}
              fetchOptions={fetchOptions}
              limit={limit}
              page={currentPage}
              setPage={setCurrentPage}
              virtualScroll={virtualScroll}
              optionZIndex={optionZIndex}
              scrollPositionRef={scrollPositionRef}
              position={position}
              isCombobox={isCombobox}
              highlightedIndex={highlightedIndex}
              handleNestedOptions={handleNestedOptions}
              triggerParentSelect={triggerParentSelect}
              setTriggerParentSelect={setTriggerParentSelect}
              optionsScrollRef={optionsScrollRef}
            />,
            portalContainer
          )
        : null}
      {errors &&
        name &&
        (get(errors, `${name}.type`) as string) === 'required' &&
        !isOpenOptions && (
          <span
            className="text-error-500 text-xxs font-primary font-normal left-0 top-full mt-1"
            style={{ ...customStyles }}
          >
            {NAMESPACE?.REQUIRED_FIELD}
          </span>
        )}
      {errors &&
        name &&
        (get(errors, `${name}.type`) as string) !== 'required' &&
        !isOpenOptions && (
          <span
            className="text-error-500 text-xxs font-primary font-normal left-0 top-full mt-1"
            style={{ ...customStyles }}
          >
            {errors?.[name]?.['message']?.toString()}
          </span>
        )}
    </div>
  )
}

const ControlledDropDown = <T, U extends FieldValues>({
  dataQaId,
  control,
  name,
  isSearchable = false,
  errors,
  onChange,
  label = '',
  options = [],
  className = '',
  disabled = false,
  required,
  placeholder = 'Select Option',
  isCustomSearch = false,
  isBordered = true,
  onSearchChange = noop,
  onFocus = noop,
  isCrossable = true,
  defaultValue,
  fetchOptions,
  limit,
  page,
  setPage,
  virtualScroll,
  tabIndex,
  confirmationModal,
  isCombobox = false,
  customStyles = {},
  height,
  customPadding = '',
  borderClassName = '',
  isPreviewAllowed = { value: false, className: '', onClick: noop },
  handleNestedOptions,
  customValidation,
  allowCloseOnScroll = false,
  highlightClassName = '',
  isHelperTextVisible = '',
  enableTypeToJump = false,
  enableTypeToFilter = false,
  minimumCharactersToFilter = 1,
  ...rest
}: ControlledDropDownPropTypes<T, U>) => {
  return (
    <Controller
      name={name}
      control={control}
      rules={{
        required: required,
        validate: (value) => {
          if (customValidation) {
            return customValidation(value, options, required)
          }
          return true
        },
      }}
      // disabled={disabled} TODO : Not required, can handle it explicitly
      render={({ field }) => (
        <div
          className={`relative w-[100%] focus:outline-none ${className} ${highlightClassName}`}
        >
          {/* This hidden input helps with form submission and focus management */}
          <input
            type="hidden"
            name={field.name}
            value={field.value}
            ref={field.ref}
          />
          <SelectDropDown
            {...rest}
            name={field?.name}
            disabled={disabled}
            label={label}
            errors={errors as FieldErrors<U>}
            defaultValue={defaultValue ?? field?.value}
            isSearchable={isSearchable}
            options={options}
            virtualScroll={virtualScroll}
            fetchOptions={fetchOptions}
            limit={limit}
            page={page}
            setPage={setPage}
            required={required}
            className={className}
            isBordered={isBordered}
            onChange={(selectedOption) => {
              field.onChange(selectedOption.value)
              onChange(selectedOption, name)
            }}
            onFocus={onFocus}
            placeholder={placeholder}
            isCustomSearch={isCustomSearch}
            onSearchChange={onSearchChange}
            isCrossable={isCrossable}
            dataQaId={dataQaId}
            confirmationModal={confirmationModal}
            isCombobox={isCombobox}
            tabIndex={tabIndex}
            customStyles={{ ...customStyles }}
            height={height}
            customPadding={customPadding}
            borderClassName={borderClassName}
            isPreviewAllowed={isPreviewAllowed}
            handleNestedOptions={handleNestedOptions}
            allowCloseOnScroll={allowCloseOnScroll}
            enableTypeToJump={enableTypeToJump}
            enableTypeToFilter={enableTypeToFilter}
            minimumCharactersToFilter={minimumCharactersToFilter}
          />
          {isHelperTextVisible ? (
            <div
              className="ck-content break-words"
              dangerouslySetInnerHTML={{ __html: isHelperTextVisible }}
            ></div>
          ) : null}
        </div>
      )}
    />
  )
}

export default ControlledDropDown
